/* eslint-disable max-lines */
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { LiveActivity, type LiveActivityPayload } from "@clipboardhealth/capacitor-live-activity";

const ACTIVITY_ID = "test-activity";

export const TestResult = {
  SUCCESS: "passed",
  FAILURE: "failed",
  PENDING: "pending",
} as const;

export type TestResultType = (typeof TestResult)[keyof typeof TestResult];

export const SCENARIO_KEYS = {
  PRE_DEPARTURE_NO_TIMING: "pre-departure-no-timing",
  PRE_DEPARTURE_EARLY: "pre-departure-early",
  PRE_DEPARTURE_LEAVE_SOON: "pre-departure-leave-soon",
  PRE_DEPARTURE_LEAVE_NOW: "pre-departure-leave-now",
  PRE_DEPARTURE_RUNNING_LATE: "pre-departure-running-late",
  PRE_DEPARTURE_STALE_ETA: "pre-departure-stale-eta",
  LIVE_ETA_VIEW: "live-eta-view",
  POST_TRIP_ARRIVAL: "post-trip-arrival",
  END_LIVE_ACTIVITY: "end-live-activity",
} as const;

export type ScenarioKey = (typeof SCENARIO_KEYS)[keyof typeof SCENARIO_KEYS];

export interface LiveActivityScenario {
  key: ScenarioKey;
  execute: () => Promise<TestResultType>;
  displayName: string;
  status: TestResultType;
}

interface BuildScenariosParams {
  onActivityStart: () => void;
  onActivityEnd: () => void;
}

async function createOrUpdateActivity(data: LiveActivityPayload): Promise<TestResultType> {
  const currentState = await LiveActivity.getCurrentState({
    activityId: ACTIVITY_ID,
  });

  if (currentState.activities.length > 0) {
    try {
      await LiveActivity.updateActivity({
        data,
        activityId: ACTIVITY_ID,
      });
      return TestResult.SUCCESS;
    } catch {
      return TestResult.FAILURE;
    }
  }

  try {
    const createResult = (await LiveActivity.createAndStartActivity({
      data,
      activityId: ACTIVITY_ID,
    })) as { success: boolean };

    return createResult.success ? TestResult.SUCCESS : TestResult.FAILURE;
  } catch {
    return TestResult.FAILURE;
  }
}

function createBaseTestData(): Omit<LiveActivityPayload, "view" | "trip"> {
  return {
    schemaVersion: "1.0.0",
    worker: {
      id: "test-worker-456",
    },
    workplace: {
      name: "Memorial Hospital",
      address: {
        formattedAddress: "5767 Mission St, San Francisco, CA 94112, USA",
        longitude: "-122.4495",
        latitude: "37.7099",
      },
      timezone: "America/Los_Angeles",
    },
    shift: {
      id: "68fa1514e65237cd415dd023",
      start: new Date(Date.now() + 1_800_000).toISOString(),
      end: new Date(Date.now() + 30_600_000).toISOString(),
    },
    clipboardScore: {
      clockInOnTime: 5,
      latenessPenalty: 35,
    },
  };
}

async function preDepartureEarlyNoTiming(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: "early",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function preDepartureEarly(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      leaveByTime: new Date(Date.now() + 1_500_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: "early",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function preDepartureLeaveSoon(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      leaveByTime: new Date(Date.now() + 600_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: "leaveSoon",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function preDepartureLeaveNow(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      leaveByTime: new Date(Date.now() + 120_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: "leaveNow",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function preDepartureRunningLate(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      leaveByTime: new Date(Date.now() - 600_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: "late",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function preDepartureStaleEta(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "PRE_DEPARTURE_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 1_800_000).toISOString(),
      leaveByTime: new Date(Date.now() + 1_500_000).toISOString(),
      tripProgressPercentage: 0,
      lastEtaReceivedAt: new Date(Date.now() - 600_000).toISOString(),
      isLastReceivedEtaFresh: false,
      preDepartureShiftViewTimingStatus: "late",
    },
  };

  return await createOrUpdateActivity(testData);
}

async function liveEtaView(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "LIVE_ETA_FOR_TRIP_SHIFT_VIEW",
    trip: {
      expectedArrivalTime: new Date(Date.now() + 900_000).toISOString(),
      leaveByTime: new Date(Date.now() - 300_000).toISOString(),
      tripProgressPercentage: 0.65,
      lastEtaReceivedAt: new Date().toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: undefined,
    },
  };

  return await createOrUpdateActivity(testData);
}

async function postTripArrivalView(): Promise<TestResultType> {
  const testData: LiveActivityPayload = {
    ...createBaseTestData(),
    view: "POST_TRIP_ARRIVAL_SHIFT_VIEW",
    shift: {
      id: "test-shift-123",
      start: new Date(Date.now() + 300_000).toISOString(),
      end: new Date(Date.now() + 29_100_000).toISOString(),
    },
    trip: {
      expectedArrivalTime: new Date(Date.now() - 120_000).toISOString(),
      leaveByTime: new Date(Date.now() - 1_200_000).toISOString(),
      tripProgressPercentage: 1,
      lastEtaReceivedAt: new Date(Date.now() - 120_000).toISOString(),
      isLastReceivedEtaFresh: true,
      preDepartureShiftViewTimingStatus: undefined,
    },
  };

  return await createOrUpdateActivity(testData);
}

async function endLiveActivity(): Promise<TestResultType> {
  try {
    const result = (await LiveActivity.endActivity({
      activityId: ACTIVITY_ID,
    })) as { success: boolean };

    return result.success ? TestResult.SUCCESS : TestResult.FAILURE;
  } catch {
    return TestResult.FAILURE;
  }
}

/**
 * Builds the list of Live Activity scenarios used by the debugger component.
 *
 * @param params - Scenario lifecycle callbacks.
 * @returns List of scenarios in their initial pending state.
 */
export function buildLiveActivityScenarios({
  onActivityStart,
  onActivityEnd,
}: BuildScenariosParams): LiveActivityScenario[] {
  return [
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_NO_TIMING,
      execute: async () => {
        const result = await preDepartureEarlyNoTiming();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "1. Pre-Departure: No Timing (Points)",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_EARLY,
      execute: async () => {
        const result = await preDepartureEarly();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "2. Pre-Departure: Early (>20 min)",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_LEAVE_SOON,
      execute: async () => {
        const result = await preDepartureLeaveSoon();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "3. Pre-Departure: Leave Soon (5-20 min)",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_LEAVE_NOW,
      execute: async () => {
        const result = await preDepartureLeaveNow();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "4. Pre-Departure: Leave Now (±5 min)",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_RUNNING_LATE,
      execute: async () => {
        const result = await preDepartureRunningLate();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "5. Pre-Departure: Running Late",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.PRE_DEPARTURE_STALE_ETA,
      execute: async () => {
        const result = await preDepartureStaleEta();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "6. Pre-Departure: Stale ETA",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.LIVE_ETA_VIEW,
      execute: async () => {
        const result = await liveEtaView();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "7. Live ETA: Trip in Progress",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.POST_TRIP_ARRIVAL,
      execute: async () => {
        const result = await postTripArrivalView();
        if (result === TestResult.SUCCESS) {
          onActivityStart();
        }

        return result;
      },
      displayName: "8. Post-Trip: Arrived",
      status: TestResult.PENDING,
    },
    {
      key: SCENARIO_KEYS.END_LIVE_ACTIVITY,
      execute: async () => {
        const result = await endLiveActivity();
        if (result === TestResult.SUCCESS) {
          onActivityEnd();
        }

        return result;
      },
      displayName: "9. End Live Activity",
      status: TestResult.PENDING,
    },
  ];
}
/* eslint-enable max-lines */
